import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import axios from "axios";

// URL para o GeoJSON dos estados brasileiros
const geoUrl = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";

const BrazilMapChart = ({ statesData, year, isMobile = false, popData = [], pibData = [] }) => {
    const navigate = useNavigate();
    const [hoveredState, setHoveredState] = useState(null);
    const [hoveredListItem, setHoveredListItem] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const [products, setProducts] = useState({});
    const mapContainerRef = useRef(null);
    const [viewMode, setViewMode] = useState("population"); // "trade" | "population" | "pib"

    // Mapa de população por sigla
    const popMap = useMemo(() => {
        const m = {};
        popData.forEach((e) => { if (e.sigla) m[e.sigla] = e; });
        return m;
    }, [popData]);

    // Mapa de PIB por sigla (dados já agregados por UF)
    const pibMap = useMemo(() => {
        const m = {};
        pibData.forEach((e) => {
            if (e.uf) m[e.uf] = { pibTotal: e.pibTotal || 0, count: e.totalMunicipios || 0, populacao: e.populacao || 0, vabAgro: e.vabAgro || 0, vabIndustria: e.vabIndustria || 0, vabServicos: e.vabServicos || 0 };
        });
        return m;
    }, [pibData]);

    // Escala de cores para PIB
    const { pibColorScale, pibMax } = useMemo(() => {
        const vals = Object.values(pibMap).map(v => v.pibTotal);
        if (vals.length === 0) return { pibColorScale: null, pibMax: 0 };
        const maxVal = Math.max(...vals, 1);
        return {
            pibColorScale: scaleLinear()
                .domain([0, maxVal * 0.1, maxVal * 0.4, maxVal])
                .range(["#ecfdf5", "#34d399", "#059669", "#064e3b"]),
            pibMax: maxVal,
        };
    }, [pibMap]);

    const formatBRL = (val) => {
        if (val >= 1e9) return `R$ ${(val / 1e9).toFixed(1)} tri`;
        if (val >= 1e6) return `R$ ${(val / 1e6).toFixed(1)} bi`;
        if (val >= 1e3) return `R$ ${(val / 1e3).toFixed(1)} mi`;
        return `R$ ${val.toFixed(0)} mil`;
    };

    // Escala de cores para população
    const { popColorScale, popMax } = useMemo(() => {
        if (popData.length === 0) return { popColorScale: null, popMax: 0 };
        const pops = popData.filter(e => e.tipo === 'estado').map(e => e.populacao);
        const maxVal = Math.max(...pops, 1);
        return {
            popColorScale: scaleLinear()
                .domain([0, maxVal * 0.15, maxVal * 0.5, maxVal])
                .range(["#f3e8ff", "#c084fc", "#7c3aed", "#4c1d95"]),
            popMax: maxVal
        };
    }, [popData]);

    const formatPop = (val) => new Intl.NumberFormat("pt-BR").format(val);

    // Calcula o domínio dinamicamente baseado nos dados reais
    const { colorScale, minValue, maxValue } = useMemo(() => {
        if (!statesData || statesData.length === 0) {
            return {
                colorScale: scaleLinear().domain([-1, 0, 1]).range(["#ff5252", "#f5f5f5", "#2980b9"]),
                minValue: -1,
                maxValue: 1
            };
        }

        const balances = statesData.map(s => parseFloat(s.balance) || 0);
        const min = Math.min(...balances);
        const max = Math.max(...balances);

        // Garante que o zero esteja no centro da escala
        const absMax = Math.max(Math.abs(min), Math.abs(max));
        const domainMin = min < 0 ? -absMax : 0;
        const domainMax = max > 0 ? absMax : 0;

        return {
            colorScale: scaleLinear()
                .domain([domainMin, 0, domainMax])
                .range(["#c0392b", "#ecf0f1", "#2980b9"]),
            minValue: domainMin,
            maxValue: domainMax
        };
    }, [statesData]);

    const formatUSD = (val) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(val);

    const formatUSDFull = (val) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(val);

    const fetchProducts = async (stateSigla) => {
        const cacheKey = `${year}-${stateSigla}`;

        // Se já temos os produtos em cache, não busca novamente
        if (products[cacheKey]) {
            return products[cacheKey];
        }

        try {
            const res = await axios.get(`/comex/state-products/${year}/${stateSigla}`);
            if (res.data.success) {
                const productsData = res.data.data;
                setProducts(prev => ({
                    ...prev,
                    [cacheKey]: productsData
                }));
                return productsData;
            }
        } catch (error) {
            console.error("Erro ao carregar produtos do estado", error);
            return null;
        }
    };

    const handleMouseEnter = async (geo, stateInfo) => {
        setHoveredState(geo.rsmKey);
        const stateSigla = geo.properties.sigla;
        const pop = popMap[stateSigla];

        if (viewMode === "population") {
            setTooltip({
                state: geo.properties.name || stateSigla,
                sigla: stateSigla,
                populacao: pop?.populacao || 0,
                mode: "population",
            });
            return;
        }

        if (viewMode === "pib") {
            const pib = pibMap[stateSigla];
            setTooltip({
                state: geo.properties.name || stateSigla,
                sigla: stateSigla,
                pibTotal: pib?.pibTotal || 0,
                municipios: pib?.count || 0,
                populacao: pop?.populacao || 0,
                mode: "pib",
            });
            return;
        }

        if (stateInfo) {
            const total = parseFloat(stateInfo.total_export) + parseFloat(stateInfo.total_import);
            const exportPercent = total > 0
                ? ((parseFloat(stateInfo.total_export) / total) * 100).toFixed(1)
                : 0;

            const cacheKey = `${year}-${stateSigla}`;

            // Verifica se já temos os produtos em cache
            const cachedProducts = products[cacheKey];

            setTooltip({
                state: geo.properties.name || stateSigla,
                sigla: stateSigla,
                export: parseFloat(stateInfo.total_export),
                import: parseFloat(stateInfo.total_import),
                balance: parseFloat(stateInfo.balance),
                exportPercent,
                populacao: pop?.populacao || null,
                products: cachedProducts || null,
                loadingProducts: !cachedProducts,
                mode: "trade",
            });

            // Se não temos em cache, busca os produtos do estado
            if (!cachedProducts) {
                const productsData = await fetchProducts(stateSigla);
                if (productsData) {
                    setTooltip(prev => ({
                        ...prev,
                        products: productsData,
                        loadingProducts: false
                    }));
                } else {
                    setTooltip(prev => ({
                        ...prev,
                        loadingProducts: false
                    }));
                }
            }
        }
    };

    const handleMouseLeave = () => {
        setHoveredState(null);
        setTooltip(null);
    };

    // Gera os ticks da legenda (menos ticks em mobile)
    const legendTicks = useMemo(() => {
        const ticks = [];
        const numTicks = isMobile ? 5 : 7;
        for (let i = 0; i <= numTicks; i++) {
            const value = minValue + (maxValue - minValue) * (i / numTicks);
            ticks.push(value);
        }
        return ticks;
    }, [minValue, maxValue, isMobile]);

    // Top 5 estados por exportação
    const topStates = useMemo(() => {
        if (!statesData || statesData.length === 0) return [];
        return [...statesData]
            .sort((a, b) => parseFloat(b.total_export) - parseFloat(a.total_export))
            .slice(0, 5)
            .map(state => ({
                ...state,
                total_export: parseFloat(state.total_export),
                total_import: parseFloat(state.total_import),
                balance: parseFloat(state.balance)
            }));
    }, [statesData]);

    // Top 5 estados por população
    const topPopStates = useMemo(() => {
        return [...popData]
            .filter(e => e.tipo === 'estado')
            .sort((a, b) => b.populacao - a.populacao)
            .slice(0, 5);
    }, [popData]);

    // Top 5 estados por PIB
    const topPibStates = useMemo(() => {
        return Object.entries(pibMap)
            .map(([uf, d]) => ({ uf, pibTotal: d.pibTotal, count: d.count }))
            .sort((a, b) => b.pibTotal - a.pibTotal)
            .slice(0, 5);
    }, [pibMap]);

    const hasPopData = popData.length > 0;
    const hasPibData = Object.keys(pibMap).length > 0;

    return (
        <div style={containerStyle(isMobile)}>
            {/* Título + Toggle */}
            <div style={overlayTitleStyle(isMobile)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                        <h3 style={titleStyle(isMobile)}>
                            {viewMode === "population"
                                ? (isMobile ? "População — IBGE 2025" : "Concentração Populacional — IBGE 2025")
                                : viewMode === "pib"
                                ? (isMobile ? "PIB Municipal — 2021" : "Produto Interno Bruto por Estado — 2021")
                                : (isMobile ? `Saldo Comercial — ${year || "Anual"}` : `Saldo Comercial por Estado — ${year || "Anual"}`)}
                        </h3>
                        {!isMobile && (
                            <p style={subtitleStyle(isMobile)}>
                                Passe o mouse sobre os estados para ver detalhes
                            </p>
                        )}
                    </div>
                    {hasPopData && (
                        <div style={{
                            display: 'flex',
                            background: '#ecf0f1',
                            borderRadius: '8px',
                            padding: '3px',
                            gap: '2px',
                        }}>
                            <button
                                onClick={() => setViewMode("trade")}
                                style={{
                                    padding: isMobile ? '4px 10px' : '6px 14px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    background: viewMode === 'trade' ? '#2980b9' : 'transparent',
                                    color: viewMode === 'trade' ? '#fff' : '#7f8c8d',
                                }}
                            >
                                💰 Comércio
                            </button>
                            <button
                                onClick={() => setViewMode("population")}
                                style={{
                                    padding: isMobile ? '4px 10px' : '6px 14px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    background: viewMode === 'population' ? '#7c3aed' : 'transparent',
                                    color: viewMode === 'population' ? '#fff' : '#7f8c8d',
                                }}
                            >
                                👥 População
                            </button>
                            {hasPibData && (
                                <button
                                    onClick={() => setViewMode("pib")}
                                    style={{
                                        padding: isMobile ? '4px 10px' : '6px 14px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        background: viewMode === 'pib' ? '#059669' : 'transparent',
                                        color: viewMode === 'pib' ? '#fff' : '#7f8c8d',
                                    }}
                                >
                                    📊 PIB
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Container flex para mapa e top states */}
            <div style={mapAndListWrapperStyle(isMobile)}>
                <div
                    ref={mapContainerRef}
                    style={mapContainerStyle(isMobile)}
                >
                    <ComposableMap
                        projection="geoMercator"
                        projectionConfig={{
                            scale: isMobile ? 600 : 750,
                            center: [-55, -15]
                        }}
                        style={{ width: "100%", height: "100%" }}
                    >
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map((geo) => {
                                    const sigla = geo.properties.sigla;
                                    const stateInfo = statesData?.find((s) => s.sg_uf_ncm === sigla);
                                    const balance = stateInfo ? parseFloat(stateInfo.balance) : null;
                                    const isHovered = hoveredState === geo.rsmKey;
                                    const hasTradeData = stateInfo !== undefined;
                                    const pop = popMap[sigla];

                                    let fillColor;
                                    let hoverFill;
                                    if (viewMode === "population") {
                                        fillColor = pop && popColorScale ? popColorScale(pop.populacao) : "#e2e8f0";
                                        hoverFill = fillColor;
                                    } else if (viewMode === "pib") {
                                        const pib = pibMap[sigla];
                                        fillColor = pib && pibColorScale ? pibColorScale(pib.pibTotal) : "#e2e8f0";
                                        hoverFill = fillColor;
                                    } else {
                                        fillColor = hasTradeData ? colorScale(balance) : "#bdc3c7";
                                        hoverFill = hasTradeData ? colorScale(balance) : "#95a5a6";
                                    }

                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill={fillColor}
                                            stroke={isHovered ? "#2c3e50" : "#ffffff"}
                                            strokeWidth={isHovered ? 2 : 1}
                                            style={{
                                                default: {
                                                    outline: "none",
                                                    transition: "all 0.2s ease"
                                                },
                                                hover: {
                                                    fill: hoverFill,
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    stroke: "#2c3e50",
                                                    strokeWidth: 2
                                                },
                                                pressed: { outline: "none" },
                                            }}
                                            onMouseEnter={() => handleMouseEnter(geo, stateInfo)}
                                            onMouseLeave={handleMouseLeave}
                                            onMouseMove={(e) => {
                                                if (mapContainerRef.current) {
                                                    const rect = mapContainerRef.current.getBoundingClientRect();
                                                    setTooltip(prev => ({
                                                        ...prev,
                                                        x: e.clientX - rect.left,
                                                        y: e.clientY - rect.top
                                                    }));
                                                }
                                            }}
                                            onClick={() => {
                                                if (geo.properties.sigla) {
                                                    navigate(`/estado/${geo.properties.sigla}/${year || 2025}`);
                                                }
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ComposableMap>

                    {/* Tooltip */}
                    {tooltip && tooltip.x !== undefined && tooltip.y !== undefined && (
                        <div
                            style={{
                                ...tooltipStyle(isMobile),
                                position: 'absolute',
                                left: `${tooltip.x}px`,
                                top: `${tooltip.y}px`,
                                transform: isMobile
                                    ? 'translate(-50%, -100%)'
                                    : 'translate(-50%, calc(-100% - 10px))',
                                maxWidth: isMobile ? '90vw' : 'none',
                            }}
                        >
                            <div style={tooltipHeaderStyle(isMobile)}>
                                <strong>{tooltip.state} ({tooltip.sigla})</strong>
                            </div>
                            <div style={tooltipContentStyle(isMobile)}>
                                {/* PIB mode tooltip */}
                                {tooltip.mode === "pib" ? (
                                    <>
                                        <div style={tooltipRowStyle(isMobile)}>
                                            <span style={{ color: "#059669" }}>📊 PIB Total:</span>
                                            <strong>{formatBRL(tooltip.pibTotal)}</strong>
                                        </div>
                                        {tooltip.populacao > 0 && (
                                            <div style={tooltipRowStyle(isMobile)}>
                                                <span style={{ color: "#7f8c8d" }}>👥 População:</span>
                                                <strong>{formatPop(tooltip.populacao)}</strong>
                                            </div>
                                        )}
                                        {tooltip.populacao > 0 && tooltip.pibTotal > 0 && (
                                            <div style={{ ...tooltipRowStyle(isMobile), borderTop: "1px solid #ecf0f1", paddingTop: 8, marginTop: 8 }}>
                                                <span>PIB per capita:</span>
                                                <strong style={{ color: "#059669" }}>
                                                    R$ {((tooltip.pibTotal / tooltip.populacao) * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
                                                </strong>
                                            </div>
                                        )}
                                        <div style={{ fontSize: "0.75rem", color: "#7f8c8d", marginTop: 4 }}>
                                            {tooltip.municipios} municípios
                                        </div>
                                        <div style={{
                                            borderTop: "1px solid #ecf0f1", marginTop: 10, paddingTop: 10,
                                            textAlign: "center", fontSize: "0.75rem", color: "#059669", fontWeight: "600"
                                        }}>
                                            {isMobile ? "👆 Toque para ver detalhes" : "👆 Clique para ver detalhes"}
                                        </div>
                                    </>
                                ) : tooltip.mode === "population" ? (
                                    <>
                                        <div style={tooltipRowStyle(isMobile)}>
                                            <span style={{ color: "#7c3aed" }}>👥 População:</span>
                                            <strong>{formatPop(tooltip.populacao)}</strong>
                                        </div>
                                        {popMax > 0 && (
                                            <div style={{ marginTop: '8px' }}>
                                                <div style={{ height: '6px', borderRadius: '3px', background: '#ecf0f1', overflow: 'hidden' }}>
                                                    <div style={{
                                                        height: '100%',
                                                        borderRadius: '3px',
                                                        background: 'linear-gradient(to right, #c084fc, #7c3aed)',
                                                        width: `${Math.max((tooltip.populacao / popMax) * 100, 1)}%`,
                                                        transition: 'width 0.3s',
                                                    }} />
                                                </div>
                                                <div style={{ fontSize: '0.7rem', color: '#7f8c8d', marginTop: '4px', textAlign: 'right' }}>
                                                    {((tooltip.populacao / popMax) * 100).toFixed(1)}% do maior estado
                                                </div>
                                            </div>
                                        )}
                                        <div style={{
                                            borderTop: "1px solid #ecf0f1",
                                            marginTop: "10px",
                                            paddingTop: "10px",
                                            textAlign: "center",
                                            fontSize: isMobile ? "0.7rem" : "0.75rem",
                                            color: "#7c3aed",
                                            fontWeight: "600"
                                        }}>
                                            {isMobile ? "👆 Toque para ver detalhes" : "👆 Clique para ver detalhes"}
                                        </div>
                                    </>
                                ) : (
                                <>
                                <div style={tooltipRowStyle(isMobile)}>
                                    <span style={{ color: "#27ae60" }}>📤 {isMobile ? "Exp:" : "Exportação:"}</span>
                                    <strong>{isMobile ? formatUSD(tooltip.export) : formatUSDFull(tooltip.export)}</strong>
                                </div>
                                <div style={tooltipRowStyle(isMobile)}>
                                    <span style={{ color: "#e67e22" }}>📥 {isMobile ? "Imp:" : "Importação:"}</span>
                                    <strong>{isMobile ? formatUSD(tooltip.import) : formatUSDFull(tooltip.import)}</strong>
                                </div>
                                <div style={{ ...tooltipRowStyle(isMobile), borderTop: "1px solid #ecf0f1", paddingTop: isMobile ? "6px" : "8px", marginTop: isMobile ? "6px" : "8px" }}>
                                    <span style={{ fontWeight: "bold" }}>Saldo:</span>
                                    <strong style={{
                                        color: tooltip.balance >= 0 ? "#2980b9" : "#c0392b"
                                    }}>
                                        {isMobile ? formatUSD(tooltip.balance) : formatUSDFull(tooltip.balance)}
                                    </strong>
                                </div>
                                {tooltip.populacao && (
                                    <div style={{ ...tooltipRowStyle(isMobile), fontSize: "0.85rem", color: "#7f8c8d", marginTop: "4px" }}>
                                        <span>👥 População:</span>
                                        <span style={{ fontWeight: '600' }}>{formatPop(tooltip.populacao)}</span>
                                    </div>
                                )}
                                {!isMobile && (
                                    <div style={{ ...tooltipRowStyle(isMobile), fontSize: "0.85rem", color: "#7f8c8d", marginTop: "4px" }}>
                                        <span>Participação Exportação:</span>
                                        <span>{tooltip.exportPercent}%</span>
                                    </div>
                                )}

                                {/* Top Produtos */}
                                {tooltip.products && (
                                    <>
                                        <div style={{
                                            borderTop: "1px solid #ecf0f1",
                                            marginTop: isMobile ? "8px" : "10px",
                                            paddingTop: isMobile ? "8px" : "10px"
                                        }}>
                                            <div style={{
                                                fontSize: isMobile ? "0.7rem" : "0.8rem",
                                                fontWeight: "bold",
                                                color: "#2c3e50",
                                                marginBottom: isMobile ? "4px" : "8px"
                                            }}>
                                                📤 Top Exportações:
                                            </div>
                                            {tooltip.products.exports && tooltip.products.exports.length > 0 ? (
                                                tooltip.products.exports.slice(0, isMobile ? 3 : 5).map((item, idx) => (
                                                    <div key={idx} style={{
                                                        ...tooltipRowStyle(isMobile),
                                                        fontSize: isMobile ? "0.65rem" : "0.75rem",
                                                        marginBottom: "3px",
                                                        alignItems: "flex-start"
                                                    }}>
                                                        <span style={{
                                                            flex: 1,
                                                            color: "#7f8c8d",
                                                            lineHeight: "1.2"
                                                        }}>
                                                            {idx + 1}. {item.product.length > (isMobile ? 20 : 35)
                                                                ? `${item.product.substring(0, isMobile ? 20 : 35)}...`
                                                                : item.product}
                                                        </span>
                                                        <strong style={{
                                                            color: "#27ae60",
                                                            marginLeft: "6px",
                                                            whiteSpace: "nowrap",
                                                            fontSize: isMobile ? "0.65rem" : "0.75rem"
                                                        }}>
                                                            {formatUSD(item.value)}
                                                        </strong>
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ fontSize: isMobile ? "0.65rem" : "0.75rem", color: "#95a5a6", fontStyle: "italic" }}>
                                                    Sem dados
                                                </div>
                                            )}
                                        </div>

                                        <div style={{
                                            borderTop: "1px solid #ecf0f1",
                                            marginTop: isMobile ? "6px" : "10px",
                                            paddingTop: isMobile ? "6px" : "10px"
                                        }}>
                                            <div style={{
                                                fontSize: isMobile ? "0.7rem" : "0.8rem",
                                                fontWeight: "bold",
                                                color: "#2c3e50",
                                                marginBottom: isMobile ? "4px" : "8px"
                                            }}>
                                                📥 Top Importações:
                                            </div>
                                            {tooltip.products.imports && tooltip.products.imports.length > 0 ? (
                                                tooltip.products.imports.slice(0, isMobile ? 3 : 5).map((item, idx) => (
                                                    <div key={idx} style={{
                                                        ...tooltipRowStyle(isMobile),
                                                        fontSize: isMobile ? "0.65rem" : "0.75rem",
                                                        marginBottom: "3px",
                                                        alignItems: "flex-start"
                                                    }}>
                                                        <span style={{
                                                            flex: 1,
                                                            color: "#7f8c8d",
                                                            lineHeight: "1.2"
                                                        }}>
                                                            {idx + 1}. {item.product.length > (isMobile ? 20 : 35)
                                                                ? `${item.product.substring(0, isMobile ? 20 : 35)}...`
                                                                : item.product}
                                                        </span>
                                                        <strong style={{
                                                            color: "#e67e22",
                                                            marginLeft: "6px",
                                                            whiteSpace: "nowrap",
                                                            fontSize: isMobile ? "0.65rem" : "0.75rem"
                                                        }}>
                                                            {formatUSD(item.value)}
                                                        </strong>
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ fontSize: isMobile ? "0.65rem" : "0.75rem", color: "#95a5a6", fontStyle: "italic" }}>
                                                    Sem dados
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {tooltip.loadingProducts && (
                                    <div style={{
                                        borderTop: "1px solid #ecf0f1",
                                        marginTop: isMobile ? "8px" : "10px",
                                        paddingTop: isMobile ? "8px" : "10px",
                                        textAlign: "center",
                                        fontSize: "0.75rem",
                                        color: "#7f8c8d"
                                    }}>
                                        Carregando produtos...
                                    </div>
                                )}

                                <div style={{
                                    borderTop: "1px solid #ecf0f1",
                                    marginTop: isMobile ? "8px" : "10px",
                                    paddingTop: isMobile ? "8px" : "10px",
                                    textAlign: "center",
                                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                                    color: "#2980b9",
                                    fontWeight: "600"
                                }}>
                                    {isMobile ? "👆 Toque para ver detalhes" : "👆 Clique para ver detalhes"}
                                </div>
                                </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Top 5 Estados */}
                {viewMode === "trade" && topStates.length > 0 && (
                    <div style={topStatesContainerStyle(isMobile)}>
                        <h4 style={topStatesTitleStyle(isMobile)}>Top 5 Estados por Exportação</h4>
                        <div style={topStatesListStyle(isMobile)}>
                            {topStates.map((state, idx) => (
                                <div
                                    key={state.sg_uf_ncm}
                                    style={topStateItemStyle(isMobile, hoveredListItem === state.sg_uf_ncm)}
                                    onClick={() => navigate(`/estado/${state.sg_uf_ncm}/${year || 2025}`)}
                                    onMouseEnter={() => setHoveredListItem(state.sg_uf_ncm)}
                                    onMouseLeave={() => setHoveredListItem(null)}
                                >
                                    <div style={topStateRankStyle(isMobile)}>
                                        {idx + 1}
                                    </div>
                                    <div style={topStateInfoStyle(isMobile)}>
                                        <div style={topStateNameStyle(isMobile)}>
                                            {state.sg_uf_ncm}
                                        </div>
                                        <div style={topStateValueStyle(isMobile)}>
                                            {formatUSD(state.total_export)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Top 5 Estados por PIB */}
                {viewMode === "pib" && topPibStates.length > 0 && (
                    <div style={topStatesContainerStyle(isMobile)}>
                        <h4 style={topStatesTitleStyle(isMobile)}>Top 5 Estados por PIB</h4>
                        <div style={topStatesListStyle(isMobile)}>
                            {topPibStates.map((state, idx) => (
                                <div
                                    key={state.uf}
                                    style={topStateItemStyle(isMobile, hoveredListItem === state.uf)}
                                    onClick={() => navigate(`/estado/${state.uf}/${year || 2025}`)}
                                    onMouseEnter={() => setHoveredListItem(state.uf)}
                                    onMouseLeave={() => setHoveredListItem(null)}
                                >
                                    <div style={{ ...topStateRankStyle(isMobile), background: '#059669' }}>
                                        {idx + 1}
                                    </div>
                                    <div style={topStateInfoStyle(isMobile)}>
                                        <div style={topStateNameStyle(isMobile)}>
                                            {state.uf}
                                        </div>
                                        <div style={{ ...topStateValueStyle(isMobile), color: '#059669' }}>
                                            {formatBRL(state.pibTotal)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Top 5 Estados por População */}
                {viewMode === "population" && topPopStates.length > 0 && (
                    <div style={topStatesContainerStyle(isMobile)}>
                        <h4 style={topStatesTitleStyle(isMobile)}>Top 5 Estados por População</h4>
                        <div style={topStatesListStyle(isMobile)}>
                            {topPopStates.map((state, idx) => (
                                <div
                                    key={state.sigla}
                                    style={topStateItemStyle(isMobile, hoveredListItem === state.sigla)}
                                    onClick={() => navigate(`/estado/${state.sigla}/${year || 2025}`)}
                                    onMouseEnter={() => setHoveredListItem(state.sigla)}
                                    onMouseLeave={() => setHoveredListItem(null)}
                                >
                                    <div style={{ ...topStateRankStyle(isMobile), background: '#7c3aed' }}>
                                        {idx + 1}
                                    </div>
                                    <div style={topStateInfoStyle(isMobile)}>
                                        <div style={topStateNameStyle(isMobile)}>
                                            {state.sigla}
                                        </div>
                                        <div style={{ ...topStateValueStyle(isMobile), color: '#7c3aed' }}>
                                            {formatPop(state.populacao)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Legenda */}
            <div style={legendContainerStyle(isMobile)}>
                {viewMode === "pib" ? (
                    <>
                        <div style={legendLabelStyle(isMobile)}>
                            <span style={{ color: "#ecfdf5", fontWeight: "bold", textShadow: "0 0 2px rgba(0,0,0,0.3)" }}>Menor</span>
                            <span style={{ margin: "0 8px" }}>⟵</span>
                            <span style={{ fontWeight: "bold", color: "#059669" }}>📊 PIB (R$ mil)</span>
                            <span style={{ margin: "0 8px" }}>⟶</span>
                            <span style={{ color: "#064e3b", fontWeight: "bold" }}>Maior</span>
                        </div>
                        <div style={legendWrapperStyle(isMobile)}>
                            <div style={{ ...legendBarGradientStyle(isMobile), background: 'linear-gradient(to right, #ecfdf5, #34d399, #059669, #064e3b)' }} />
                            <div style={legendTicksContainerStyle}>
                                {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => (
                                    <div key={i} style={legendTickContainerStyle}>
                                        <div style={legendTickMarkerStyle} />
                                        <div style={legendValueStyle(isMobile)}>
                                            {formatBRL(Math.round(pibMax * frac))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : viewMode === "population" ? (
                    <>
                        <div style={legendLabelStyle(isMobile)}>
                            <span style={{ color: "#f3e8ff", fontWeight: "bold", textShadow: "0 0 2px rgba(0,0,0,0.3)" }}>Menor</span>
                            <span style={{ margin: "0 8px" }}>⟵</span>
                            <span style={{ fontWeight: "bold", color: "#7c3aed" }}>👥 População</span>
                            <span style={{ margin: "0 8px" }}>⟶</span>
                            <span style={{ color: "#4c1d95", fontWeight: "bold" }}>Maior</span>
                        </div>
                        <div style={legendWrapperStyle(isMobile)}>
                            <div
                                style={{
                                    ...legendBarGradientStyle(isMobile),
                                    background: 'linear-gradient(to right, #f3e8ff, #c084fc, #7c3aed, #4c1d95)'
                                }}
                            />
                            <div style={legendTicksContainerStyle}>
                                {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => (
                                    <div key={i} style={legendTickContainerStyle}>
                                        <div style={legendTickMarkerStyle} />
                                        <div style={legendValueStyle(isMobile)}>
                                            {formatPop(Math.round(popMax * frac))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={legendLabelStyle(isMobile)}>
                            {isMobile ? (
                                <>
                                    <span style={{ color: "#c0392b", fontWeight: "bold", fontSize: "0.8rem" }}>Negativo</span>
                                    <span style={{ margin: "0 4px", fontSize: "0.8rem" }}>⟵</span>
                                    <span style={{ fontWeight: "bold", fontSize: "0.8rem" }}>0</span>
                                    <span style={{ margin: "0 4px", fontSize: "0.8rem" }}>⟶</span>
                                    <span style={{ color: "#2980b9", fontWeight: "bold", fontSize: "0.8rem" }}>Positivo</span>
                                </>
                            ) : (
                                <>
                                    <span style={{ color: "#c0392b", fontWeight: "bold" }}>Saldo Negativo</span>
                                    <span style={{ margin: "0 8px" }}>⟵</span>
                                    <span style={{ fontWeight: "bold" }}>0</span>
                                    <span style={{ margin: "0 8px" }}>⟶</span>
                                    <span style={{ color: "#2980b9", fontWeight: "bold" }}>Saldo Positivo</span>
                                </>
                            )}
                        </div>
                        <div style={legendWrapperStyle(isMobile)}>
                            <div
                                style={{
                                    ...legendBarGradientStyle(isMobile),
                                    background: `linear-gradient(to right, 
                                        ${colorScale(minValue)}, 
                                        ${colorScale(0)}, 
                                        ${colorScale(maxValue)}
                                    )`
                                }}
                            />
                            <div style={legendTicksContainerStyle}>
                                {legendTicks.map((tick, i) => (
                                    <div key={i} style={legendTickContainerStyle}>
                                        <div style={legendTickMarkerStyle} />
                                        <div style={legendValueStyle(isMobile)}>
                                            {formatUSD(tick)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {!isMobile && (
                            <div style={legendNoteStyle}>
                                <span style={{ color: "#bdc3c7" }}>■</span> Sem dados disponíveis
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const containerStyle = (isMobile) => ({
    background: "#fff",
    padding: isMobile ? "12px" : "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    marginBottom: isMobile ? "12px" : "20px",
    display: "flex",
    flexDirection: "column"
});

const overlayTitleStyle = (isMobile) => ({
    position: "relative",
    zIndex: 10,
    background: "transparent",
    padding: isMobile ? "0 0 10px 0" : "0 0 12px 0",
    borderRadius: "8px",
    marginBottom: isMobile ? "10px" : "12px"
});

const titleStyle = (isMobile) => ({
    color: "#2c3e50",
    margin: 0,
    fontSize: isMobile ? "0.85rem" : "1rem",
    fontWeight: "600"
});

const subtitleStyle = (isMobile) => ({
    fontSize: isMobile ? "0.7rem" : "0.75rem",
    color: "#7f8c8d",
    margin: "4px 0 0 0"
});

const mapAndListWrapperStyle = (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "10px" : "15px",
    alignItems: isMobile ? "stretch" : "flex-start"
});

const mapContainerStyle = (isMobile) => ({
    width: "100%",
    height: isMobile ? "350px" : "500px",
    position: "relative",
    flex: isMobile ? "none" : "1"
});

const tooltipStyle = (isMobile) => ({
    background: "#fff",
    padding: isMobile ? "10px 12px" : "12px 16px",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    zIndex: 1000,
    pointerEvents: "none",
    minWidth: isMobile ? "180px" : "280px",
    maxWidth: isMobile ? "90vw" : "400px",
    border: "1px solid #ecf0f1"
});

const tooltipHeaderStyle = (isMobile) => ({
    fontSize: isMobile ? "0.9rem" : "1rem",
    color: "#2c3e50",
    marginBottom: isMobile ? "8px" : "10px",
    paddingBottom: isMobile ? "6px" : "8px",
    borderBottom: "2px solid #ecf0f1"
});

const tooltipContentStyle = (isMobile) => ({
    fontSize: isMobile ? "0.8rem" : "0.9rem"
});

const tooltipRowStyle = (isMobile) => ({
    display: "flex",
    justifyContent: "space-between",
    marginBottom: isMobile ? "4px" : "6px",
    gap: isMobile ? "8px" : "12px"
});

const legendContainerStyle = (isMobile) => ({
    marginTop: isMobile ? "8px" : "12px",
    paddingTop: isMobile ? "8px" : "12px",
    borderTop: "1px solid #ecf0f1"
});

const legendLabelStyle = (isMobile) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: isMobile ? "8px" : "12px",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    color: "#2c3e50",
    flexWrap: isMobile ? "wrap" : "nowrap"
});

const legendWrapperStyle = (isMobile) => ({
    position: "relative",
    width: "100%",
    maxWidth: isMobile ? "100%" : "600px",
    margin: "0 auto 12px"
});

const legendBarGradientStyle = (isMobile) => ({
    width: "100%",
    height: isMobile ? "20px" : "24px",
    borderRadius: "4px",
    marginBottom: isMobile ? "6px" : "8px"
});

const legendTicksContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    position: "relative",
    width: "100%"
};

const legendTickContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1
};

const legendTickMarkerStyle = {
    width: "1px",
    height: "6px",
    background: "#7f8c8d",
    marginBottom: "2px"
};

const legendValueStyle = (isMobile) => ({
    fontSize: isMobile ? "0.65rem" : "0.75rem",
    color: "#7f8c8d",
    marginTop: "4px",
    textAlign: "center"
});

const legendNoteStyle = {
    textAlign: "center",
    fontSize: "0.8rem",
    color: "#7f8c8d",
    fontStyle: "italic"
};

const topStatesContainerStyle = (isMobile) => ({
    padding: isMobile ? "10px" : "12px",
    background: "#f8f9fa",
    borderRadius: "8px",
    flex: isMobile ? "none" : "0 0 280px",
    minWidth: isMobile ? "100%" : "250px",
    maxWidth: isMobile ? "100%" : "320px"
});

const topStatesTitleStyle = (isMobile) => ({
    margin: "0 0 10px 0",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: "600",
    color: "#2c3e50"
});

const topStatesListStyle = (isMobile) => ({
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "6px" : "8px"
});

const topStateItemStyle = (isMobile, isHovered) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "8px" : "12px",
    padding: isMobile ? "6px 8px" : "8px 12px",
    background: "#fff",
    borderRadius: "6px",
    boxShadow: isHovered ? "0 2px 6px rgba(0,0,0,0.1)" : "0 1px 3px rgba(0,0,0,0.05)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    transform: isHovered ? "translateY(-1px)" : "translateY(0)"
});

const topStateRankStyle = (isMobile) => ({
    width: isMobile ? "20px" : "24px",
    height: isMobile ? "20px" : "24px",
    borderRadius: "50%",
    background: "#2980b9",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? "0.7rem" : "0.8rem",
    fontWeight: "bold",
    flexShrink: 0
});

const topStateInfoStyle = (isMobile) => ({
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
});

const topStateNameStyle = (isMobile) => ({
    fontSize: isMobile ? "0.75rem" : "0.85rem",
    fontWeight: "600",
    color: "#2c3e50"
});

const topStateValueStyle = (isMobile) => ({
    fontSize: isMobile ? "0.7rem" : "0.8rem",
    fontWeight: "bold",
    color: "#27ae60"
});

export default BrazilMapChart;